/**
 * Service API pour la récupération des produits depuis le backend
 * Utilisé par le renderer pour charger dynamiquement les produits multi-parts
 */

import { preprocessFormulaAngles } from './evalCacheDimFormula';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RULE_MATH_PROXY = new Proxy(Math, {
  get(target, prop) {
    if (prop === 'radians') return (deg) => (deg * Math.PI) / 180;
    return Reflect.get(target, prop);
  },
});

let _publicProductsPromise = null;
let _publicProductsCacheTs = 0;
const PUBLIC_PRODUCTS_TTL = 60000;

/**
 * Récupère la liste des produits publics avec leurs règles d'affichage.
 * Déduplique les appels concurrents et cache le résultat pendant 60s.
 * @returns {Promise<Array>} Liste des produits
 */
export const fetchPublicProducts = async () => {
  const now = Date.now();
  if (_publicProductsPromise && (now - _publicProductsCacheTs) < PUBLIC_PRODUCTS_TTL) {
    return _publicProductsPromise;
  }

  _publicProductsCacheTs = now;
  _publicProductsPromise = (async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/products/public`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`Failed to fetch products: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('[ProductsApi] Error fetching public products:', error);
      _publicProductsPromise = null;
      return [];
    }
  })();
  return _publicProductsPromise;
};

/**
 * Récupère la configuration complète d'un produit (parts + métadonnées)
 * @param {number} productId - ID du produit
 * @returns {Promise<Object|null>} Configuration du produit ou null
 */
export const fetchProductConfig = async (productId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/products/${productId}/config`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch product config: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[ProductsApi] Error fetching product config:', error);
    return null;
  }
};

/**
 * Sélectionne automatiquement le produit approprié selon les dimensions via API backend
 * ⚠️ CONVENTION BACKEND pour les paramètres (correspondant aux expressions displayRule):
 *    sizeX = profondeur (X dans les expressions)
 *    sizeY = largeur (Y dans les expressions)
 *    sizeZ = hauteur (Z dans les expressions)
 * @param {number} sizeX - Profondeur (convention backend)
 * @param {number} sizeY - Largeur (convention backend)
 * @param {number} sizeZ - Hauteur (convention backend)
 * @returns {Promise<Object|null>} Produit sélectionné ou null
 */
export const selectProductByDimensions = async (sizeX, sizeY, sizeZ) => {
  try {
    const response = await fetch(`${BACKEND_URL}/products/select`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sizeX, sizeY, sizeZ }),
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.warn('[ProductsApi] No product matches dimensions:', { sizeX, sizeY, sizeZ });
        return null;
      }
      throw new Error(`Failed to select product: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[ProductsApi] Error selecting product:', error);
    return null;
  }
};

/**
 * Évalue une expression de règle d'affichage côté client.
 *
 * ⚠️ La signature DOIT rester strictement alignée sur celle du backoffice
 *    (usipanel-backoffice/app/(dashboard)/products/page.jsx → evaluateExpression)
 *    sinon une règle créée en BO comme `HTOP && BOTTOM` plante silencieusement
 *    côté client (ReferenceError: HTOP is not defined) et le produit n'est
 *    jamais sélectionné.
 *
 * Variables disponibles :
 *   X, Y, Z   : dimensions (number) — convention BACKEND (X=profondeur, Y=largeur, Z=hauteur)
 *   BOTTOM    : avec fond                (bool)
 *   HTOP      : façade haute             (bool)
 *   WALLMOUNT : montage mural            (bool) — état indépendant du TYPE
 *                                                 (toggle "fixation murale/au sol")
 *   REAR      : avec face arrière        (bool)
 *   TYPE      : type d'installation      (string) — alias historique de INSTALL
 *   INSTALL   : type d'installation      (string : 'sol', 'sol-mur', 'mur', 'mur-haut')
 *   DEC       : alias strict de DECi (rétrocompat)
 *   DECi      : décalage dérivé du type d'installation
 *   DECc      : décalage spécifique au produit (null si non configuré)
 *   Math/math : Math standard + math.radians(deg) ; 2° / 2deg acceptés
 */
export const evaluateDisplayRule = (expression, X, Y, Z, options = {}) => {
  if (!expression || expression.trim() === '') return true;

  try {
    const DECi = options.DECi ?? options.DEC ?? 0;
    const DECc = options.DECc ?? null;
    const DEC = options.DEC ?? DECi;
    const safeExpression = preprocessFormulaAngles(expression);
    // eslint-disable-next-line no-new-func
    const fn = new Function(
      'X', 'Y', 'Z', 'BOTTOM', 'HTOP', 'WALLMOUNT', 'REAR',
      'TYPE', 'DEC', 'DECi', 'DECc', 'INSTALL', 'Math', 'math',
      `return (${safeExpression})`
    );
    const installValue = options.INSTALL ?? options.TYPE ?? '';
    return Boolean(fn(
      X, Y, Z,
      !!options.BOTTOM, !!options.HTOP, !!options.WALLMOUNT, !!options.REAR,
      options.TYPE || installValue || '',
      DEC, DECi, DECc,
      installValue,
      Math,
      RULE_MATH_PROXY,
    ));
  } catch (error) {
    console.warn('[ProductsApi] Invalid expression:', expression, error);
    return false;
  }
};

/**
 * Sélectionne le produit approprié côté client
 * @param {Array} products - Liste des produits avec règles
 * @param {number} sizeX - Profondeur (convention backend X)
 * @param {number} sizeY - Largeur (convention backend Y)
 * @param {number} sizeZ - Hauteur (convention backend Z)
 * @param {Object} options - { BOTTOM, TYPE, DEC }
 * @returns {Object|null} Produit sélectionné ou null
 */
export const selectProductLocally = (products, sizeX, sizeY, sizeZ, options = {}) => {
  const debug = buildProductSelectionDebug(products, sizeX, sizeY, sizeZ, options);
  return debug.selectedProduct;
};

/**
 * Détail de la sélection produit (évaluation locale des displayRule, par priorité).
 */
export function buildProductSelectionDebug(products, sizeX, sizeY, sizeZ, options = {}) {
  const dimensionsBackend = {
    X: Number(sizeX),
    Y: Number(sizeY),
    Z: Number(sizeZ),
    label: 'X=profondeur, Y=largeur, Z=hauteur avant (convention règles BO)',
  };

  const displayOptionsSnapshot = {
    BOTTOM: !!options.BOTTOM,
    HTOP: !!options.HTOP,
    WALLMOUNT: !!options.WALLMOUNT,
    REAR: !!options.REAR,
    TYPE: options.TYPE ?? options.INSTALL ?? '',
    INSTALL: options.INSTALL ?? options.TYPE ?? '',
    DEC: options.DEC ?? 0,
    DECi: options.DECi ?? options.DEC ?? 0,
    DECc: options.DECc ?? null,
  };

  if (!products || products.length === 0) {
    return {
      dimensionsBackend,
      displayOptions: displayOptionsSnapshot,
      selectedProduct: null,
      productEvaluations: [],
    };
  }

  const sorted = [...products].sort(
    (a, b) => (a.displayRule?.priority ?? 999) - (b.displayRule?.priority ?? 999),
  );

  let selectedProduct = null;
  const productEvaluations = sorted.map((product) => {
    const expression = product.displayRule?.expression ?? '';
    const matches = evaluateDisplayRule(expression, sizeX, sizeY, sizeZ, options);
    if (matches && !selectedProduct) {
      selectedProduct = product;
    }
    return {
      id: product.id,
      name: product.name ?? `Produit #${product.id}`,
      priority: product.displayRule?.priority ?? 999,
      expression: expression || '(vide → toujours vrai)',
      matches,
      isWinner: false,
      refDimensions: {
        sizeX: product.sizeX,
        sizeY: product.sizeY,
        sizeZ: product.sizeZ,
      },
    };
  });

  const winner = productEvaluations.find((e) => e.matches);
  if (winner) winner.isWinner = true;

  return {
    dimensionsBackend,
    displayOptions: displayOptionsSnapshot,
    selectedProduct: selectedProduct
      ? {
          id: selectedProduct.id,
          name: selectedProduct.name,
          sizeX: selectedProduct.sizeX,
          sizeY: selectedProduct.sizeY,
          sizeZ: selectedProduct.sizeZ,
          displayRule: selectedProduct.displayRule,
        }
      : null,
    productEvaluations,
  };
}

